import { resolveRelayerUrl } from "../../relayers/resolve";
import { FeatureFlagKey } from "../../features/types";
import { ChainIdEnum } from "../../../enums/chain";
import { WalletProviderKind } from "../../../enums/walletProvider";
import { ChainConfig } from "../types";
import {
  REALTIME_BRANDING,
  RPC_WSS_URL,
  envRpcUrls,
  getPerpetualDexConfig,
  getTokenAddress,
  getUsdcAddress,
  toRpcWebSocketUrl,
} from "./shared";

// ============================================
// ARC TESTNET (Chain ID: 5042002)
// ============================================
export const arcTestnetConfig: ChainConfig = {
  chainId: ChainIdEnum.ARC_TESTNET,
  name: "Arc Testnet",
  shortName: "Arc",
  isTestnet: true,
  // Arc's only RPC is a keyed endpoint, so it is never committed: the URL comes
  // from REACT_APP_RPC_URL at build time (assertOnBoot fails the build without it).
  rpcUrls: envRpcUrls(),
  rpcWsUrls: [
    RPC_WSS_URL || toRpcWebSocketUrl(process.env.REACT_APP_RPC_URL) || "",
  ].filter(Boolean),
  wsUrl: process.env.REACT_APP_WSS_URL,
  explorerUrl: "https://testnet.arcscan.app",
  l2ExplorerUrl: "https://testnet.arcscan.app",
  nativeCurrency: { name: "USDC", symbol: "USDC", decimals: 18 },
  nativeIsQuoteToken: true,
  tokenDecimals: {
    usdc: 18,   // Native USDC wrapped as WNATIVE (18 decimals)
    usdt: 18,
    weth: 18,
    wbtc: 8,
    sdai: 6,
  },
  contracts: {
    erc20: {
      usdc: getUsdcAddress(ChainIdEnum.ARC_TESTNET, "0xb247310B996F618990ac440a41fAA167b1C65EEb"),
      "usdc-native": "",
      usdt: "",
      weth: getTokenAddress(ChainIdEnum.ARC_TESTNET, "WETH", "0xF3E2e78c0693eB3a54734e601275e41715E3CB0E"),
      wbtc: getTokenAddress(ChainIdEnum.ARC_TESTNET, "WBTC", "0x111BC129616B48c12cc2B99808086D58d0a10C6b"),
      l2usdc: "",
      l2usdt: "",
      l2weth: "",
      l2wbtc: "",
      govToken: "",
    },
    bridge: {
      l1DepositHelper: "",
      l1StandardBridge: "",
      l2StandardBridge: "",
      l2WithdrawProxy: "",
      exchangePass: "",
      strategies: "",
      staking: "",
    },
    perpetualDex: getPerpetualDexConfig(ChainIdEnum.ARC_TESTNET, {
      // NOTE: these are only fallback defaults — getPerpetualDexConfig
      // overrides them from src/deployments/5042002.json (synced from
      // clob-dex) and throws for the active chain rather than use stale
      // values. Kept in sync with the current Arc deployment anyway.
      router: "0xda4DF629ad7FC158A2eCea0B90B56C21399C05BE",
      crossMargin: "",
      orderHandler: "0x2CF5adcAD8c8cbfe0844540Ab9AfE2FD84B6a28A",
      orderVault: "",
      marketFactory: "0x86F7d1a1D024a23609D6E6232B491b68BBF1f319",
      usdc: "0xb247310B996F618990ac440a41fAA167b1C65EEb",
      unifiedVault: "0xeDC5F4cEc726144eA8700Ebb5540Cb18aA920d60",
      unifiedMarginAdapter: "",
      positionHandler: "0x1df34cC2602D1acfEF24f9792DD024acDC0D9Bc5",
      backstopVault: "0x5dBf22b69FE622F594C187bE32c0Ef9641cC156F",
      oracle: "0xD1C8b041F23cB5a95770C833465e7CD35d57a932",
      dataStore: "0x306c553a39Ff7728c8428Df94Af05a1Fb2EBE37C",
      marketHandler: "0x87e26361333E9E7C321BEb663476326986dA1EF1",
      balanceManager: "0x970e3AfF9ce197F0602392C35285319eAbac610C",
      perpRouter: "0xda4DF629ad7FC158A2eCea0B90B56C21399C05BE",
      markets: {
        "BTC-PERP": "0x059973D04134B326FB9714B2e16bf6Dc329A5dBF",
        "ETH-PERP": "0x6fC40493143954bBa171D03ce6A692F690741D1B",
      },
    }),
  },
  signingDomain: { name: "RealTime Arc Testnet", version: "1" },
  // Ultrafuzz M-1: Arc's CLOBRouter market-order functions gained `maxQuoteIn`. Use the V2
  // signed struct + the 6-param direct call args (see useSpotOrder.place.ts).
  spotMarketMaxQuoteIn: true,
  gasless: true,
  relayerUrl: resolveRelayerUrl(ChainIdEnum.ARC_TESTNET),
  supportsSubaccounts: false,
  supportsTpsl: true,
  walletProvider: WalletProviderKind.PARA,
  // Spot CLOB is deployed here, so the spot vertical is available (it is off by
  // default and opted into per chain). See src/config/features.
  tradingTypes: {
    [FeatureFlagKey.PERPETUAL_SPOT]: true,
  },

  branding: REALTIME_BRANDING,
};
